/**
 * This file provides a list of Activity Log Results column display values based on event_type
 */
import { get } from 'lodash';
import AssociationEventConsts from 'client/Insights/ActivityLog/constants/AssociationEventConsts';
import Constants from 'client/util/Constants';
import EventTypes from 'client/Insights/ActivityLog/constants/EventTypes';
import ItemTypeConstants from 'client/util/Constants/ItemTypeConstants';

const { dataNotAvailable } = Constants;
const {
  command,
  group,
  organizations,
  policy,
  system,
  systemUser,
} = ItemTypeConstants.ItemTypeDisplayNames;

function mapGetDisplayValue(data, textIfTrue, textIfFalse) {
  if (String(data.success) === 'true') {
    return textIfTrue;
  }
  if (String(data.success) === 'false') {
    return textIfFalse;
  }
  return dataNotAvailable;
}

const displayValues = {
  [EventTypes.activeDirectoryCreate]: { displayValue: 'AD domain created' },
  [EventTypes.activeDirectoryDelete]: { displayValue: 'AD domain deleted' },
  [EventTypes.adminCreate]: { displayValue: 'Admin created' },
  [EventTypes.adminDelete]: { displayValue: 'Admin deleted' },
  [EventTypes.adminLoginAttempt]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'Admin login successful',
      'Admin login failed',
    ),
  },
  [EventTypes.adminOldApiKeyAttempt]: { displayValue: 'Admin API key failed' },
  [EventTypes.adminPasswordChange]: { displayValue: 'Admin password changed' },
  [EventTypes.adminPasswordResetRequest]: { displayValue: 'Admin requested to reset password' },
  [EventTypes.adminUpdate]: { displayValue: 'Admin account updated' },
  [EventTypes.applicationCreate]: { displayValue: 'SAML application created' },
  [EventTypes.applicationDelete]: { displayValue: 'SAML application deleted' },
  [EventTypes.applicationUpdate]: { displayValue: 'SAML application modified' },
  [EventTypes.associationChange]: {
    getDisplayValue: (d) => {
      const { resourceToTextMap } = AssociationEventConsts;

      let action;
      const childObj = get(d, 'association.connection.to.type', '');
      const childObject = childObj.toUpperCase();
      const parentObj = get(d, 'association.connection.from.type', '');
      const parentObject = parentObj.toUpperCase();
      const op = get(d, 'association.op', '');

      if (!resourceToTextMap[childObject]
        || !resourceToTextMap[parentObject]
        || !op) return dataNotAvailable;

      const child = resourceToTextMap[childObject].charAt(0).toUpperCase()
        + resourceToTextMap[childObject].slice(1);
      const parent = resourceToTextMap[parentObject];

      if (d.association.op === 'remove') {
        action = 'removed from';
      } else if (d.association.op === 'add') {
        action = 'added to';
      } else if (d.association.op === 'update') {
        action = 'updated on';
      }

      return `${child} ${action} ${parent}`;
    },
  },
  [EventTypes.authnFallbackPolicyCreate]: { displayValue: 'Default access policy created' },
  [EventTypes.authnFallbackPolicyDelete]: { displayValue: 'Default access policy deleted' },
  [EventTypes.authnFallbackPolicyUpdate]: { displayValue: 'Default access policy updated' },
  [EventTypes.authnPolicyCreate]: { displayValue: 'Conditional access policy created' },
  [EventTypes.authnPolicyDelete]: { displayValue: 'Conditional access policy deleted' },
  [EventTypes.authnPolicyUpdate]: { displayValue: 'Conditional access policy updated' },
  [EventTypes.commandCreate]: { displayValue: `${command} created` },
  [EventTypes.commandDelete]: { displayValue: `${command} deleted` },
  [EventTypes.commandResultDelete]: { displayValue: `${command} result deleted` },
  [EventTypes.commandRun]: { displayValue: `${command} executed` },
  [EventTypes.commandUpdate]: { displayValue: `${command} updated` },
  [EventTypes.deviceCommand]: {
    getDisplayValue: (eventData) => mapGetDisplayValue(
      eventData,
      'Command send successful',
      'Command send failed',
    ),
  },
  [EventTypes.fileCreate]: { displayValue: `${command} file uploaded` },
  [EventTypes.fileDelete]: { displayValue: `${command} file deleted` },
  [EventTypes.fileUpdate]: { displayValue: `${command} file updated` },
  [EventTypes.googleEmmCreate]: {
    getDisplayValue: (eventData) => mapGetDisplayValue(
      eventData,
      'EMM registration successful',
      'EMM registration failed',
    ),
  },
  [EventTypes.googleEmmDelete]: {
    getDisplayValue: (eventData) => mapGetDisplayValue(
      eventData,
      'EMM delete successful',
      'EMM delete failed',
    ),
  },
  [EventTypes.googleEmmPatch]: {
    getDisplayValue: (eventData) => mapGetDisplayValue(
      eventData,
      'EMM patch successful',
      'EMM patch failed',
    ),
  },
  [EventTypes.groupCreate]: { displayValue: `${group} created` },
  [EventTypes.groupDelete]: { displayValue: `${group} deleted` },
  [EventTypes.groupUpdate]: { displayValue: `${group} updated` },
  [EventTypes.gsuiteDirectoryCreate]: { displayValue: 'Gsuite directory created' },
  [EventTypes.gsuiteDirectoryDelete]: { displayValue: 'Gsuite directory deleted' },
  [EventTypes.gsuiteDirectoryUpdate]: { displayValue: 'Gsuite directory updated' },
  [EventTypes.idpCreate]: { displayValue: 'Identity provider created' },
  [EventTypes.idpUpdate]: { displayValue: 'Identity provider updated' },
  [EventTypes.idpDelete]: { displayValue: 'Identity provider deleted' },
  [EventTypes.idSourceCreate]: { displayValue: 'AD Bridge V1 created' },
  [EventTypes.idSourceDelete]: { displayValue: 'AD Bridge V1 deleted' },
  [EventTypes.idSourceUpdate]: { displayValue: 'AD Bridge V1 updated' },
  [EventTypes.integrationActivate]: { displayValue: 'SCIM integration activated' },
  [EventTypes.integrationDeactivate]: { displayValue: 'SCIM integration deactivated' },
  [EventTypes.integrationDefaultDomainAdd]: {
    getDisplayValue: (eventData) => mapGetDisplayValue(
      eventData,
      'Default domain set',
      'Default domain not set',
    ),
  },
  [EventTypes.integrationDefaultDomainDelete]: {
    getDisplayValue: (eventData) => mapGetDisplayValue(
      eventData,
      'Default domain unset',
      'Default domain unset failed',
    ),
  },
  [EventTypes.integtrationDefaultDomainUpdate]: {
    getDisplayValue: (eventData) => mapGetDisplayValue(
      eventData,
      'Default domain changed',
      'Default domain change failed',
    ),
  },
  [EventTypes.integrationDomainAdd]: {
    getDisplayValue: (eventData) => mapGetDisplayValue(
      eventData,
      'Domain added',
      'Domain add failed',
    ),
  },
  [EventTypes.integrationDomainDelete]: {
    getDisplayValue: (eventData) => mapGetDisplayValue(
      eventData,
      'Domain deleted',
      'Domain delete failed',
    ),
  },
  [EventTypes.integrationattributeInclude]: { displayValue: 'Attribute mapping created' },
  [EventTypes.integrationattributeExclude]: { displayValue: 'Attribute mapping deleted' },
  [EventTypes.iplistCreate]: { displayValue: 'IP address list created' },
  [EventTypes.iplistDelete]: { displayValue: 'IP address list deleted' },
  [EventTypes.iplistUpdate]: { displayValue: 'IP address list updated' },
  [EventTypes.ldapBind]: {
    getDisplayValue: (d) => {
      if (String(d.success) === 'true') {
        return 'LDAP bind successful';
      }
      if (String(d.success) === 'false') {
        return 'LDAP bind failed';
      }
      return dataNotAvailable;
    },
  },
  [EventTypes.ldapSearch]: { displayValue: 'LDAP search executed' },
  [EventTypes.loginAttempt]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      `${system} login successful`,
      `${system} login failed`,
    ),
  },
  [EventTypes.mdmCommandResult]: {
    getDisplayValue: (d) => {
      const mdmStatusMap = {
        Acknowledged: 'MDM command acknowledged',
        Idle: 'MDM command idle',
        Error: 'MDM command error',
        CommandFormatError: 'MDM command format error',
      };
      return mdmStatusMap[d.status] || dataNotAvailable;
    },
  },
  [EventTypes.passwordManagerBackupCreate]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      "A user's backup file uploaded to the cloud",
      "A user's backup file failed to upload to the cloud",
    ),
  },
  [EventTypes.passwordManagerBackupEnable]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'Cloud backup enabled',
      'Cloud backup enable failed',
    ),
  },
  [EventTypes.passwordManagerBackupDisable]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'Cloud backup disabled',
      'Cloud backup disable failed',
    ),
  },
  [EventTypes.passwordManagerBackupKeyCreate]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'Backup key created',
      'Backup key create failed',
    ),
  },
  [EventTypes.passwordManagerBackupKeyDelete]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'Backup key deleted',
      'Backup key delete failed',
    ),
  },
  [EventTypes.passwordManagerBackupKeyRegenerate]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'New backup key created',
      'New backup key creation failed',
    ),
  },
  [EventTypes.passwordManagerBackupRequest]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'Request to restore a cloud backup created',
      'Request to restore a cloud backup failed',
    ),
  },
  [EventTypes.passwordManagerBackupRequestApprove]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'Cloud backup request approved',
      'Cloud backup request approve failed',
    ),
  },
  [EventTypes.passwordManagerBackupRequestCancel]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'Cloud backup request canceled',
      'Cloud backup request cancel failed',
    ),
  },
  [EventTypes.passwordManagerBackupRequestReject]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'Cloud backup request rejected',
      'Cloud backup request reject failed',
    ),
  },
  [EventTypes.passwordManagerBackupReqeustRestore]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'Cloud backup restored',
      'Cloud backup restore failed',
    ),
  },
  [EventTypes.passwordManagerDisable]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'Org password manager disabled',
      'Org password manager disable failed',
    ),
  },
  [EventTypes.passwordManagerEnable]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'Org password manager enabled',
      'Org password manager enable failed',
    ),
  },
  [EventTypes.passwordManagerFolderCreate]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'Folder created',
      'Folder create failed',
    ),
  },
  [EventTypes.passwordManagerFolderDelete]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'Folder deleted',
      'Folder delete failed',
    ),
  },
  [EventTypes.passwordManagerFolderMemberAdd]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'Member added to folder',
      'Member add to folder failed',
    ),
  },
  [EventTypes.passwordManagerFolderMemberRemove]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'Member removed from folder',
      'Member remove from folder failed',
    ),
  },
  [EventTypes.passwordManagerFolderMemberUpdate]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'Member updated in folder',
      'Member update in folder failed',
    ),
  },
  [EventTypes.passwordManagerItemAdd]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'Item added to folder',
      'Item added to folder failed',
    ),
  },
  [EventTypes.passwordManagerItemMove]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'Item moved into a folder',
      'Item moved into a folder failed',
    ),
  },
  [EventTypes.passwordManagerItemRemove]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'Item removed from folder',
      'Item removed from folder failed',
    ),
  },
  [EventTypes.passwordManagerItemUpdate]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'Item updated in folder',
      'Item updated in folder failed',
    ),
  },
  [EventTypes.passwordManagerUserActivate]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'User password manager activated',
      'User password manager activate failed',
    ),
  },
  [EventTypes.passwordManagerUserDeactivate]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'User password manager deactivated',
      'User password manager deactivate failed',
    ),
  },
  [EventTypes.passwordManagerUserDevicePair]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'New device paired',
      'New device pair failed',
    ),
  },
  [EventTypes.passwordManagerUserDeviceUnpair]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'Device unpaired',
      'Device unpair failed',
    ),
  },
  [EventTypes.passwordManagerUserDisable]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'User password manager disabled',
      'User password manager disable failed',
    ),
  },
  [EventTypes.passwordManagerUserEnable]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'User password manager enabled',
      'User password manager enable failed',
    ),
  },
  [EventTypes.passwordManagerUserEnableAgain]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'User password manager re-enabled',
      'User password manager re-enable failed',
    ),
  },
  [EventTypes.passwordManagerUserReactivate]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'User password manager reactivated',
      'User password manager reactivate failed',
    ),
  },
  [EventTypes.passwordManagerUserSignup]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'User signed up',
      'User sign up failed',
    ),
  },
  [EventTypes.passwordManagerUserUpdate]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'User updated',
      'User update failed',
    ),
  },
  [EventTypes.notificationDelete]: { displayValue: 'Notification closed' },
  [EventTypes.notificationDelete]: { displayValue: 'Notification closed' },
  [EventTypes.o365DirectoryCreate]: { displayValue: 'O365 directory created' },
  [EventTypes.o365DirectoryDelete]: { displayValue: 'O365 directory deleted' },
  [EventTypes.o365DirectoryUpdate]: { displayValue: 'O365 directory updated' },
  [EventTypes.organizationCreate]: { displayValue: `${organizations} created` },
  [EventTypes.organizationUpdate]: { displayValue: `${organizations} updated` },
  [EventTypes.osMinorUpgrade]: { displayValue: 'OS minor upgraded' },
  [EventTypes.osMajorUpgrade]: { displayValue: 'OS major upgraded' },
  [EventTypes.osMinorRollback]: { displayValue: 'OS minor rolledback' },
  [EventTypes.osMajorRollback]: { displayValue: 'OS major rolledback' },
  [EventTypes.policyCreate]: { displayValue: `${policy} created` },
  [EventTypes.policyDelete]: { displayValue: `${policy} deleted` },
  [EventTypes.policyUpdate]: { displayValue: `${policy} updated` },
  [EventTypes.radiusAuthAttempt]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'RADIUS login successful',
      'RADIUS login failed',
    ),
  },
  [EventTypes.radiusServerCreate]: { displayValue: 'RADIUS server created' },
  [EventTypes.radiusServerDelete]: { displayValue: 'RADIUS server deleted' },
  [EventTypes.radiusServerUpdate]: { displayValue: 'RADIUS server updated' },
  [EventTypes.remoteSessionStart]: { displayValue: 'Remote assist session started' },
  [EventTypes.remoteSessionEnd]: { displayValue: 'Remote assist session ended' },
  [EventTypes.remoteSessionJoin]: { displayValue: 'Remote assist session joined' },
  [EventTypes.remoteSessionFeedback]: { displayValue: 'Remote assist session feedback given' },
  [EventTypes.sambaDomainCreate]: { displayValue: 'LDAP samba domain created' },
  [EventTypes.sambaDomainDelete]: { displayValue: 'LDAP samba domain deleted' },
  [EventTypes.sambaDomainUpdate]: { displayValue: 'LDAP samba domain updated' },
  [EventTypes.softwareAdd]: { displayValue: 'Software added to device' },
  [EventTypes.softwareChange]: { displayValue: 'Software changed on device' },
  [EventTypes.softwareRemove]: { displayValue: 'Software removed from device' },
  [EventTypes.ssoAuth]: { displayValue: 'SSO login attempt' },
  [EventTypes.systemCreate]: { displayValue: `${system} created` },
  [EventTypes.systemDelete]: { displayValue: `${system} deleted` },
  [EventTypes.systemFdeKeyUpdate]: { displayValue: `${system} file disk encryption key updated` },
  [EventTypes.systemFdeKeyDecrypt]: { displayValue: `${system} file disk encryption key decrypted` },
  [EventTypes.systemUpdate]: { displayValue: `${system} updated` },
  [EventTypes.translationRuleCreate]: { displayValue: 'Attribute mapping created' },
  [EventTypes.translationRuleDelete]: { displayValue: 'Attribute mapping deleted' },
  [EventTypes.trialResume]: {
    getDisplayValue: (record) => (
      record.initiated_by.type === 'admin'
        ? 'Admin resumed trial'
        : 'Resumed trial'
    ),
  },
  [EventTypes.trialStart]: {
    getDisplayValue: (record) => (
      record.initiated_by.type === 'admin'
        ? 'Admin started trial'
        : 'Started trial outside of plan'
    ),
  },
  [EventTypes.trialStop]: {
    getDisplayValue: (record) => (
      record.initiated_by.type === 'admin'
        ? 'Admin stopped trial'
        : 'Stopped a trial by upgrading'
    ),
  },
  [EventTypes.userActivated]: { displayValue: `${systemUser} activated` },
  [EventTypes.userAdminGranted]: { displayValue: `${systemUser} granted admin sudo privileges` },
  [EventTypes.userAdminRevoked]: { displayValue: `${systemUser} granted admin sudo privileges revoked` },
  [EventTypes.userCreate]: { displayValue: `${systemUser} created` },
  [EventTypes.userCreateProvision]: { displayValue: `${systemUser} provision created` },
  [EventTypes.userDeactivated]: { displayValue: `${systemUser} deactivated` },
  [EventTypes.userDelete]: { displayValue: `${systemUser} deleted` },
  [EventTypes.userDeleteProvision]: { displayValue: `${systemUser} provision deleted` },
  [EventTypes.userGroupAdminGrant]: { displayValue: 'Administrative User Group privileges granted' },
  [EventTypes.userGroupAdminRevoke]: { displayValue: 'Administrative User Group privileges revoked' },
  [EventTypes.userImportStarted]: { displayValue: `${systemUser} import Started` },
  [EventTypes.userImportCompleted]: { displayValue: `${systemUser} import Completed` },
  [EventTypes.userImportError]: { displayValue: `${systemUser} import Error` },
  [EventTypes.userLockout]: { displayValue: `${systemUser} locked out` },
  [EventTypes.userLoginAttempt]: {
    getDisplayValue: (d) => mapGetDisplayValue(
      d,
      'User Portal login successful',
      'User Portal login failed',
    ),
  },
  [EventTypes.userMfaExclusionExpired]: { displayValue: `${systemUser} MFA enrollment expired` },
  [EventTypes.userPasswordChange]: { displayValue: `${systemUser} password changed` },
  [EventTypes.userPasswordExpired]: { displayValue: `${systemUser} password expired` },
  [EventTypes.userPasswordResetRequest]: { displayValue: `${systemUser} requested to reset password` },
  [EventTypes.userPasswordWarningEmail]: { displayValue: 'Password warning email sent' },
  [EventTypes.userPasswordSet]: { displayValue: `${systemUser} password set` },
  [EventTypes.userActivationEmail]: { displayValue: `${systemUser} activation email sent` },
  [EventTypes.userActivationScheduleCreate]: { displayValue: `${systemUser} activation scheduled` },
  [EventTypes.userActivationScheduleDelete]: { displayValue: `${systemUser} scheduled activation deleted` },
  [EventTypes.userSuspended]: { displayValue: `${systemUser} suspended` },
  [EventTypes.userSuspensionScheduleCreate]: { displayValue: `${systemUser} suspension scheduled` },
  [EventTypes.userSuspensionScheduleDelete]: { displayValue: `${systemUser} scheduled suspension deleted` },
  [EventTypes.userUnsuspended]: { displayValue: `${systemUser} unsuspended` },
  [EventTypes.userUnlocked]: { displayValue: `${systemUser} unlocked` },
  [EventTypes.userUpdate]: { displayValue: `${systemUser} account updated` },
  [EventTypes.userUpdateProvision]: { displayValue: `${systemUser} provision updated` },
  [EventTypes.workdayCreate]: { displayValue: 'Workday directory created' },
  [EventTypes.workdayDelete]: { displayValue: 'Workday directory deleted' },
  [EventTypes.workdayUpdate]: { displayValue: 'Workday directory updated' },
};

export default {
  getResultFromRecord(record) {
    const metadata = displayValues[record.event_type];
    if (!metadata) return dataNotAvailable;
    if (metadata.displayValue) return metadata.displayValue;
    if (metadata.getDisplayValue) {
      const value = metadata.getDisplayValue(record);
      return (typeof value === 'undefined') ? dataNotAvailable : value;
    }
    return dataNotAvailable;
  },
};
